// THE CUT SCREEN'S ARITHMETIC (spec 07 §4.5, slice 07-C-3) — pure, exact, and
// deliberately SMALL.
//
// §4.5: "the client performs ONE honest check (do these segments still tile the
// length this snapshot shows) while naming which snapshot it came from. The
// server is the authority; §9.3 lists what it re-checks." Everything in this
// file exists to serve that sentence and the outward-only handle rule, and
// nothing in it plans a cut: `planGuidedCut` lives on the server, is called by
// BOTH the propose route and the eligibility block, and a third implementation
// on a phone is exactly the drift D-181 was written about.
//
// EVERY COMPARISON IS ON SCALED INTEGERS (D-041/D-042/D-156). Lengths arrive as
// exact decimal text mirroring `numeric(18,6)` — module 06's own scale — and
// `Number("1543.500250")` is a different drum from `1543.500250`. `parseMeasure`
// (module 06's own helper, re-used rather than re-written) turns the text into a
// bigint ×1e6; nothing here converts back except through `formatMeasure`, which
// is display only.
//
// UNPARSEABLE INPUT IS `undefined`, NEVER 0. A malformed length must degrade to
// "unknown" and make the honest check report NOT-HOLDING — a screen that treated
// garbage as zero would announce a perfect tiling of an empty cable.

import { parseMeasure } from "../../office/inventory/lib/measure";

/** The shape the tiling check needs off a `QualityCutSegment` — kept structural
 * so the pure functions are testable without a generated type. */
export interface SegmentExtent {
  readonly seq: number;
  readonly from_m: string;
  readonly to_m: string;
}

/**
 * §4.5's ONE honest check: do these segments still tile `[0, source_length_m]`
 * exactly, in `seq` order, with no gap and no overlap?
 *
 * EXACT because the cut is modelled kerf-free (D-170): module 07 omits `kerf`
 * on the module-06 call and the trim window IS the physical allowance, so there
 * is no blade width to leave a millimetre of slack for. If a later slice ever
 * charges kerf, this check is one of the places that must be reopened — §2.9's
 * `CHECK (length_m = to_m - from_m)` is the other.
 */
export function segmentsTile(
  segments: readonly SegmentExtent[],
  sourceLengthM: string | null | undefined,
): boolean {
  const total = parseMeasure(sourceLengthM);
  if (total === undefined || total <= 0n) return false;
  if (segments.length === 0) return false;
  const ordered = [...segments].sort((a, b) => a.seq - b.seq);
  let cursor = 0n;
  for (const s of ordered) {
    const from = parseMeasure(s.from_m);
    const to = parseMeasure(s.to_m);
    if (from === undefined || to === undefined) return false;
    if (from !== cursor || to <= from) return false;
    cursor = to;
  }
  return cursor === total;
}

/** The MINIMUM removal §3.5d demands, clamped to the cable:
 * `[fault − trim, fault + trim] ∩ [0, source_length]` (D-179).
 *
 * The same interval the server's `requiredWindow` computes, and the client
 * computes it for ONE purpose: to keep a handle from being dragged inward past
 * it, so the operator is told at the control instead of by a 422 after a round
 * trip. The server re-derives it and refuses `window_narrowed` regardless —
 * this is a courtesy, and the code says so rather than the screen implying it.
 *
 * `undefined` when any input is unparseable: no window means no adjustment is
 * offered, which fails closed.
 */
export function requiredWindow(input: {
  readonly sourceLengthM: string;
  readonly faultPositionM: string;
  readonly trimAllowanceM: string;
}): { readonly from: bigint; readonly to: bigint } | undefined {
  const length = parseMeasure(input.sourceLengthM);
  const fault = parseMeasure(input.faultPositionM);
  const trim = parseMeasure(input.trimAllowanceM);
  if (length === undefined || fault === undefined || trim === undefined) return undefined;
  const rawFrom = fault - trim;
  const rawTo = fault + trim;
  return { from: rawFrom < 0n ? 0n : rawFrom, to: rawTo > length ? length : rawTo };
}

/** Why an adjusted window cannot be sent, or `undefined` when it can. The three
 * answers map onto sentences the operator can act on, which is why this returns
 * a reason rather than a boolean — and each one is NAMED after the server
 * refusal it mirrors (`does_not_tile` → `unreadable`, `window_narrowed` →
 * `narrowed`, `topology_changed` → `topology_changed`), so the two files can be
 * read side by side. */
/**
 * The three refusals, as a RUNTIME array so the i18n parity gate can walk them.
 *
 * `floor-cut.tsx` builds its sentence key by template literal
 * (`quality.cut.handleProblem.${problem}`), which the gate counts among the
 * "computed key(s) skipped — not statically resolvable". Registering this array
 * in `tools/ci/i18n-parity.ts`'s DYNAMIC_FAMILIES makes a renamed or added
 * member fail the build instead of reaching an operator as a raw dotted key —
 * on the one screen whose entire purpose is that a refused adjustment says WHY.
 * Its two siblings on the same screen were registered when they were written;
 * this one was the family that got missed.
 *
 * The type is DERIVED from the array rather than declared beside it, so the
 * union and the gate cannot drift apart.
 */
export const WINDOW_PROBLEMS = ["unreadable", "narrowed", "topology_changed"] as const;

export type WindowProblem = (typeof WINDOW_PROBLEMS)[number];

/**
 * HOW MANY SEGMENTS `[0, length]` TILES INTO around the waste window
 * `[from, to]` — the count half of the server's `buildTiling`, and nothing else
 * (`apps/api/src/modules/quality/cut/cut-plan.ts`).
 *
 * The server emits a piece only when it has POSITIVE length: `from > 0` is
 * "there is an A piece", `to > from` is "there is a waste piece", `to < length`
 * is "there is a B piece". That is the WHOLE of the zero-length rule (D-179),
 * and counting is all the client needs — which kind each piece gets
 * (`outerKind` against `min_sellable_length_m`) changes no count and is the
 * server's judgement to make.
 */
export function tiledSegmentCount(length: bigint, from: bigint, to: bigint): number {
  return (from > 0n ? 1 : 0) + (to > from ? 1 : 0) + (to < length ? 1 : 0);
}

/**
 * §3.5d step 2's OUTWARD-ONLY rule and §9.3's topology rule, checked at the
 * control — **the server's own tests, in the server's own order**, so that
 * what this screen refuses and what `retileGuidedCut` refuses are the same set.
 *
 *  1. The window must lie on the cable as a real interval (the server's
 *     `does_not_tile`: `from < 0 || to > length || to <= from`). Reported as
 *     `unreadable` — "those marks are not two positions on this drum" is true
 *     of a mark past the end and of a mark typed twice, not only of garbage.
 *  2. A handle may move AWAY from the fault and never toward it (the server's
 *     `window_narrowed`): a narrowed window would leave the fault inside a
 *     piece sold as good, which is the one outcome this whole flow exists to
 *     prevent.
 *  3. The adjustment may not ADD or REMOVE a segment (the server's
 *     `topology_changed`), because `qc_cut_segments` grants no DELETE and a
 *     re-tile that drops a row has nowhere to put it. The continuation is a
 *     new propose with a wider trim (D-162), which is what the sentence says.
 *
 * **THE TEST FOR (3) IS A SEGMENT COUNT, NOT "DOES THE WINDOW TOUCH AN END"**
 * (2026-08-21 review). It was `from <= 0n || to >= length`, justified as "a
 * window touching an end consumes a whole segment" — which is only true when
 * that segment EXISTED. On a plan whose window was ALREADY CLAMPED to the
 * cable — precisely the D-179 shape this slice created, and the shape a fault
 * near a drum end always produces — there was no outer segment to consume, so
 * the server takes the adjustment happily and the FLOOR refused it. A client
 * stricter than the server is not a courtesy; it is a screen telling an
 * operator that a legal cut is illegal, on the drums where extruder faults
 * actually cluster. `currentSegmentCount` is the count the server compares
 * against (`retileGuidedCut`'s third argument — the rows the plan has now), so
 * pass `segments.length` off the live plan.
 *
 * What is deliberately NOT re-derived here: the server's `no_stock_segment`
 * refusal, which depends on `min_sellable_length_m` and on `outerKind`'s
 * judgement about each surviving piece. That is a kind decision, this file
 * counts; the server refuses it with its own sentence, and inventing a fourth
 * implementation of `outerKind` on a phone is the drift D-181 was written
 * about.
 */
export function windowProblem(input: {
  readonly sourceLengthM: string;
  readonly faultPositionM: string;
  readonly trimAllowanceM: string;
  readonly fromM: string;
  readonly toM: string;
  /** How many segments the LIVE plan has right now — `segments.length`. */
  readonly currentSegmentCount: number;
}): WindowProblem | undefined {
  const required = requiredWindow(input);
  const length = parseMeasure(input.sourceLengthM);
  const from = parseMeasure(input.fromM);
  const to = parseMeasure(input.toM);
  if (required === undefined || length === undefined || from === undefined || to === undefined) {
    return "unreadable";
  }
  if (from < 0n || to > length || to <= from) return "unreadable";
  if (from > required.from || to < required.to) return "narrowed";
  if (tiledSegmentCount(length, from, to) !== input.currentSegmentCount) return "topology_changed";
  return undefined;
}

/** A segment with the kind the server assigned it — the extent plus the one
 * field the demotion question is asked about. Structural, like
 * `SegmentExtent`, so `QualityCutSegment` satisfies it without an import. */
export interface SegmentPiece extends SegmentExtent {
  readonly kind: "stock" | "waste";
}

/**
 * WHICH WASTE PIECES WERE **DEMOTED** — lengths of real cable going to scrap
 * because they came back under `min_sellable_length_m` — as opposed to THE
 * TRIM, which is the fault being removed and is the entire point of the cut.
 *
 * This function exists because `segments.some(s => s.kind === "waste")` is
 * true of EVERY ordinary cut (2026-08-21 review): a 49.5 m / 1.0 m trim /
 * 49.5 m plan has a waste segment and nothing was demoted, so the amber strip
 * told the operator a good piece had been scrapped for being short. A sentence
 * that renders correctly and says something false is worse than no sentence —
 * read once against a plan with no short piece in it, it teaches the operator
 * that this strip means nothing.
 *
 * **THE WIRE DOES NOT CARRY THE DISTINCTION, AND IT SHOULD.**
 * `QualityCutSegment` publishes `kind: 'stock' | 'waste'` and nothing else, so
 * "why is this piece waste" is not a fact this screen is told — it is one it
 * has to re-derive, and a second implementation of a server judgement is the
 * exact drift D-181 was written about. The honest fix is a `demoted: boolean`
 * on `QualityCutSegment`, written by the server's `buildTiling`, which already
 * knows: it labels the trim `"waste"` unconditionally and asks `outerKind` for
 * the outer pieces. That is module 07's field to add and this slice owns
 * `apps/web` only, so until it exists the derivation below stands in — and it
 * is written to PROVE its claim from the numbers rather than to guess it.
 *
 * **The derivation, which must stay in step with the server's `buildTiling` /
 * `outerKind`** (`apps/api/src/modules/quality/cut/cut-plan.ts`): a plan has AT
 * MOST ONE trim piece — the waste segment covering `[fault − trim, fault +
 * trim]` clamped, which is the same segment `validateStoredTiling` looks for
 * when it asks whether the fault is still inside what is being removed, and
 * the same interval `requiredWindow` above returns. A ZERO trim produces no
 * waste piece at all (§3.5d; `from === to`). Every OTHER waste segment is
 * therefore an outer piece that `outerKind` demoted.
 *
 * **And it is not taken on trust.** A segment is reported only when it ALSO
 * measures under the minimum — the condition the sentence on screen actually
 * states. Anything unprovable reports NOTHING: an unparseable length, an
 * absent minimum (NULL imposes none — D-172), a window that will not compute.
 * Failing closed here costs at worst a missing amber on a plan whose numbers
 * are already visible in the segment list beside it; failing open costs the
 * strip its meaning.
 */
export function demotedSegments<S extends SegmentPiece>(
  segments: readonly S[],
  geometry: {
    readonly sourceLengthM: string;
    readonly faultPositionM: string;
    readonly trimAllowanceM: string;
  },
  minSellableLengthM: string | null | undefined,
): readonly S[] {
  const min = parseMeasure(minSellableLengthM);
  const required = requiredWindow(geometry);
  if (min === undefined || required === undefined) return [];
  // A zero trim removes no piece, so there is no trim segment to exclude.
  const hasTrimPiece = required.to > required.from;
  return segments.filter((segment) => {
    if (segment.kind !== "waste") return false;
    const from = parseMeasure(segment.from_m);
    const to = parseMeasure(segment.to_m);
    if (from === undefined || to === undefined || to <= from) return false;
    if (hasTrimPiece && from <= required.from && to >= required.to) return false;
    return to - from < min;
  });
}

/** A scaled measure back as exact decimal text at module 06's scale — the wire
 * form, produced without ever passing through a float. Used for the handle
 * positions the confirm sends. */
export function measureToText(scaled: bigint): string {
  const negative = scaled < 0n;
  const abs = negative ? -scaled : scaled;
  const whole = abs / 1_000_000n;
  const frac = (abs % 1_000_000n).toString().padStart(6, "0");
  return `${negative ? "-" : ""}${whole.toString()}.${frac}`;
}

/**
 * Where a mark sits along the cable as a 0–100 percentage, for the DIAGRAM
 * only.
 *
 * The one place in this file a float is allowed, and the boundary is the same
 * one `MeasureBar` draws in the execution screen: a pixel is not a verdict,
 * nothing downstream reads this, and the numbers beside the diagram come from
 * `formatMeasure` over the exact text. Clamped, so a stale mark cannot paint
 * outside the drum.
 */
export function positionPct(valueM: string, sourceLengthM: string): number | undefined {
  const value = parseMeasure(valueM);
  const total = parseMeasure(sourceLengthM);
  if (value === undefined || total === undefined || total <= 0n) return undefined;
  const clamped = value < 0n ? 0n : value > total ? total : value;
  // Scaled integers all the way to the last division: 4 dp of a ratio is
  // 0.01% of a bar, which is well under one pixel at any viewport.
  return Number((clamped * 1_000_000n) / total) / 10_000;
}
